import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Typography } from '@memoresa/ui-components';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { useState } from 'react';
import { isMobileSafari, isSafari } from 'react-device-detect';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { calcPriceParsed } from '../../utils/functions/calcPriceParsed';
import useWindowSize from '../../utils/hooks/useWindowSize';
import { ProductDetails } from '../../utils/interfaces/ProductDetails';
import { ProductActionsButton } from '../buttons/ProductActionsButton';
import { AmountStepper } from '../inputs/AmountStepper';
import { TextInput } from '../inputs/TextInput';

interface ProductWithoutVariantsProps {
  product: ProductDetails;
  handleAction: (action: {
    product: ProductDetails;
    type: string;
    currentAmount: number;
  }) => void;
}
export const ProductWithoutVariants: React.FC<ProductWithoutVariantsProps> = ({
  product,
  handleAction,
}) => {
  const { t } = useTranslation();
  const { isSm } = useWindowSize();
  const methods = useFormContext();
  const [currentAmount, setCurrentAmount] = useState(
    product.amountSelectorConfig.initial
  );
  console.log(product);
  return (
    <div className="p-4 sm:p-8 sm:col-span-3">
      <div
        data-cy={'product-title'}
        className="flex flex-col w-full text-primary"
      >
        <Typography
          className="text-[18px] sm:text-[26px] font-bold uppercase !font-heebo"
          hyphens={false}
        >
          {product.title}
        </Typography>
        <div className="py-2">
          <Typography className="text-[18px] font-semibold  ">
            {product.subTitle}
          </Typography>
        </div>

        <div
          className={clsx(
            'py-2 text-[18px] font-medium order-3 sm:order-none ',
            isSafari || isMobileSafari
              ? 'short-description-safari-only'
              : 'short-description'
          )}
        >
          {parse(product.shortDescription || '')}
        </div>
        {product.infoText && (
          <div className="flex items-center order-2 p-5 my-4 space-x-2 rounded-md text-primary bg-[#F7F7F7] sm:order-none">
            <Icon path={mdiInformationOutline} size={1.8} />
            <Typography className="font-semibold">
              {product.infoText}
            </Typography>
          </div>
        )}
        {product.customizationFields &&
          product.customizationFields.length > 0 &&
          product.customizationFields?.map(field => (
            <div className="flex flex-col pt-2 pb-4 space-y-2" key={field.name}>
              <TextInput
                register={methods.register(field.name, {
                  required: field.requiredErrorMessage,
                  pattern: {
                    value:
                      field.errorConstraints &&
                      new RegExp(field.errorConstraints[0].regex),
                    message:
                      field.errorConstraints &&
                      field.errorConstraints[0].message,
                  },
                  maxLength: {
                    value: field.maxLength - 1,
                    message: t('general.field.error.maxLength', {
                      value: field.maxLength,
                    }),
                  },
                })}
                type={field.type.toLowerCase()}
                label={field.label}
                placeholder={field.placeholder}
                required={field.required}
                disabled={field.disabled}
                maxLength={field.maxLength}
              />
              {/* Show only if there are fields to fill after the order is completed */}
              {product.hasPostProcessingStep && (
                <Typography color="primary" className="text-[14px]">
                  {t('general.words.moreInfo')}
                </Typography>
              )}
            </div>
          ))}
        <div className="flex flex-col items-center justify-between w-full sm:justify-end sm:space-y-4 my-2">
          <div className="flex flex-col items-center justify-end pb-6 space-x-2 sm:py-0 w-full">
            {product.isSubscriptionProduct && (
              <div className="relative flex flex-col sm:flex-row sm:space-x-1 w-full justify-end sm:items-end">
                {/* Add back if its posible to have one time payments here aswell */}
                {/* <div className="flex flex-col sm:w-1/2">
                <Typography className="font-medium text-[18px] text-center">
                  One-time payment
                </Typography>
                <div className="flex h-[38px] sm:h-[54px] flex-col items-center justify-center px-3 py-2 border rounded-md border-primary-20">
                  <Typography className="font-semibold text-[20px] text-center">
                    {calcPriceParsed(
                      product.priceInfo.priceInEurCents,
                      currentAmount,
                      product.priceInfo.volumeDiscountSettings
                    )}
                  </Typography>
                </div>
              </div> */}
                {product.priceInfo.line2 && (
                  <div className="flex flex-col self-end">
                    <div className="w-1/3 min-w-fit flex h-[38px] sm:h-[54px] flex-col items-center justify-center px-3 py-2 border rounded-md border-primary-20">
                      <Typography className="font-semibold text-[20px] text-center">
                        {product.priceInfo.line2}
                      </Typography>
                    </div>
                  </div>
                )}
                <Typography className="font-medium text-[14px] absolute right-0 -bottom-[9px] translate-y-1/2">
                  {t('general.words.inclVat')}
                </Typography>
              </div>
            )}
            {!product.isSubscriptionProduct && (
              <div className="relative flex sm:space-x-1 w-full justify-end items-end">
                <div className="w-1/3 min-w-fit flex h-[38px] sm:h-[54px] flex-col items-center justify-center px-3 py-2 border rounded-md border-primary-20">
                  <Typography className="font-semibold text-[20px] text-center">
                    {calcPriceParsed(
                      product.priceInfo.priceInEurCents,
                      currentAmount,
                      product.priceInfo.volumeDiscountSettings
                    )}
                  </Typography>
                </div>

                <Typography className="font-medium text-[14px] absolute right-0 -bottom-[9px] translate-y-1/2">
                  {t('general.words.inclVat')}
                </Typography>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between w-full space-x-2 sm:hidden">
            <Typography
              color={product.available ? 'secondary' : 'text-error'}
              className="text-[14px] sm:text-[18px] font-medium min-w-fit "
            >
              {product.availabilityLabel}
            </Typography>
            {(!product.amountSelectorConfig.max ||
              product.amountSelectorConfig.max > 1) &&
              !isSm && (
                <AmountStepper
                  currentAmount={currentAmount}
                  onClickLess={() => {
                    if (
                      currentAmount - product.amountSelectorConfig.stepping >=
                      product.amountSelectorConfig.min
                    ) {
                      setCurrentAmount(
                        amount => amount - product.amountSelectorConfig.stepping
                      );
                    }
                  }}
                  onClickMore={() => {
                    if (
                      currentAmount + product.amountSelectorConfig.stepping <=
                        product.amountSelectorConfig.max ||
                      !product.amountSelectorConfig.max
                    ) {
                      setCurrentAmount(
                        amount => amount + product.amountSelectorConfig.stepping
                      );
                    }
                  }}
                />
              )}
          </div>
        </div>

        <div className="flex-col items-end justify-end hidden w-full py-6 space-y-4 sm:flex">
          <div className="flex justify-end w-full space-x-2 items-center">
            <div
              className={clsx(
                'flex items-center justify-between  w-3/5',
                !product.amountSelectorConfig.max ||
                  product.amountSelectorConfig.max > 1
                  ? 'self-start'
                  : 'self-center'
              )}
            >
              <Typography
                color={product.available ? 'secondary' : 'text-error'}
                className="text-[14px] sm:text-[18px] font-medium min-w-fit "
              >
                {product.availabilityLabel}
              </Typography>
              {(!product.amountSelectorConfig.max ||
                product.amountSelectorConfig.max > 1) && (
                <AmountStepper
                  currentAmount={currentAmount}
                  onClickLess={() => {
                    if (
                      currentAmount - product.amountSelectorConfig.stepping >=
                      product.amountSelectorConfig.min
                    ) {
                      setCurrentAmount(
                        amount => amount - product.amountSelectorConfig.stepping
                      );
                    }
                  }}
                  onClickMore={() => {
                    if (
                      currentAmount + product.amountSelectorConfig.stepping <=
                        product.amountSelectorConfig.max ||
                      !product.amountSelectorConfig.max
                    ) {
                      setCurrentAmount(
                        amount => amount + product.amountSelectorConfig.stepping
                      );
                    }
                  }}
                />
              )}
            </div>
            <div className="flex flex-col w-2/5 space-y-2">
              <ProductActionsButton
                onClick={() => {
                  handleAction({
                    product: product,
                    type: 'buy',
                    currentAmount: currentAmount,
                  });
                }}
                disabled={!product.available}
                data-cy="direct-buy-button"
              >
                {t('general.words.buyNow')}
              </ProductActionsButton>
              {!product.directCheckout && (
                <ProductActionsButton
                  variant="secondary"
                  onClick={() => {
                    handleAction({
                      product: product,
                      type: 'addToCart',
                      currentAmount: currentAmount,
                    });
                    setCurrentAmount(
                      product.variants?.[0].amountSelectorConfig.initial ?? 1
                    );
                  }}
                  disabled={!product.available}
                  data-cy="add-to-cart-button"
                >
                  {t('general.words.addToCart')}
                </ProductActionsButton>
              )}
            </div>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 flex justify-center w-full px-8 py-4 space-x-6 ring-inset ring-1 sm:hidden z-[99] ring-primary bg-primary-contrast">
          {!product.directCheckout && (
            <ProductActionsButton
              variant="secondary"
              onClick={() => {
                handleAction({
                  product: product,
                  type: 'addToCart',
                  currentAmount: currentAmount,
                });
                setCurrentAmount(
                  product.variants?.[0].amountSelectorConfig.initial ?? 1
                );
              }}
              disabled={!product.available}
            >
              {t('general.words.addToCart')}
            </ProductActionsButton>
          )}
          <ProductActionsButton
            onClick={() => {
              handleAction({
                product: product,
                type: 'buy',
                currentAmount: currentAmount,
              });
            }}
            disabled={!product.available}
          >
            {t('general.words.buyNow')}
          </ProductActionsButton>
        </div>
      </div>
    </div>
  );
};
